import { useState, useCallback } from 'react'
import { useApi } from '../contexts/ApiContext'
import { validateRosterFile } from '../utils/validateRoster'
import type { RosterIssue } from '../utils/validateRoster'
import { isStructured } from './useModalFiles'
import type { ModalFile } from './useModalFiles'

export interface RosterValidationState {
  errors: RosterIssue[]
  validating: boolean
  validateThen: (files: ModalFile[], proceed: (files: ModalFile[]) => void) => Promise<void>
  clearErrors: () => void
}

/** Blocking roster check for modals that hand files on to the pipeline. */
export function useRosterValidation(): RosterValidationState {
  const api = useApi()
  const [errors, setErrors] = useState<RosterIssue[]>([])
  const [validating, setValidating] = useState(false)

  const clearErrors = useCallback(() => setErrors([]), [])

  const validateThen = useCallback(async (files: ModalFile[], proceed: (files: ModalFile[]) => void) => {
    const roster = files.find((f) => isStructured(f.name))
    if (roster) {
      setValidating(true)
      const check = await validateRosterFile(roster, (b64) => api.parseExcel(b64))
      setValidating(false)
      if (check.errors.length > 0) {
        setErrors(check.errors)
        return
      }
    }
    setErrors([])
    proceed(files)
  }, [api])

  return { errors, validating, validateThen, clearErrors }
}

export function RosterErrorList({ errors }: { errors: RosterIssue[] }): JSX.Element | null {
  if (errors.length === 0) return null
  return (
    <div role="alert" className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
      <p className="text-xs font-semibold text-red-800">
        {errors[0].fileName} cannot be analysed until these are fixed
      </p>
      <ul className="pl-3 space-y-1">
        {errors.map((notice, i) => (
          <li key={i} className="text-xs text-red-700 list-disc list-outside ml-2 whitespace-pre-wrap">
            {notice.message}
          </li>
        ))}
      </ul>
    </div>
  )
}
